import { Children, forwardRef, isValidElement, useEffect, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent, type InputHTMLAttributes, type ReactNode, type Ref, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { fittedTextRows, TEXT_ROWS_MIN } from '../../lib/text-rows';
import { cn } from '../../lib/utils';
import { Dialog } from './Dialog';

const field = 'h-10 w-full min-w-0 max-w-full rounded-lg border border-[var(--bo-border)] bg-[var(--bo-canvas)] px-3 text-sm text-[var(--bo-text)] placeholder:text-[var(--bo-faint)]';
export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) { return <input className={cn(field, className)} {...props} />; }
function assignRef<T>(ref: Ref<T> | undefined, node: T | null) {
  if (typeof ref === 'function') ref(node);
  else if (ref) (ref as { current: T | null }).current = node;
}

export const TextArea = forwardRef<HTMLTextAreaElement, Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'rows'>>(function TextArea({ className, value, ...props }, ref) {
  const inner = useRef<HTMLTextAreaElement>(null);
  const [rows, setRows] = useState(TEXT_ROWS_MIN);
  useLayoutEffect(() => {
    const node = inner.current;
    if (!node) return;
    setRows(fittedTextRows((count) => {
      node.rows = count;
      return node.scrollHeight > node.clientHeight + 1;
    }));
  }, [value]);
  return (
    <textarea
      {...props}
      ref={(node) => { assignRef(inner, node); assignRef(ref, node); }}
      rows={rows}
      value={value}
      className={cn(field, className, 'h-auto min-h-0 resize-none overflow-y-auto py-2')}
    />
  );
});

export function GrowingText({ value, onChange, disabled }: { value: string; onChange: (value: string) => void; disabled?: boolean }) {
  return <TextArea disabled={disabled} value={value} onChange={(event) => onChange(event.target.value)} />;
}

function optionText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(optionText).join('');
  return '';
}

export interface SelectChoice {
  value: string;
  label?: string;
  code?: string;
  name?: string;
  description?: string;
}

function optionList(children: ReactNode): SelectChoice[] {
  const options: SelectChoice[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement<{ value?: string | number; children?: ReactNode }>(child) || child.type !== 'option') return;
    options.push({ value: String(child.props.value ?? ''), label: optionText(child.props.children) || String(child.props.value ?? '') });
  });
  return options;
}

function part(value?: string) {
  return (value || '').trim();
}

export function choiceLabel(choice: SelectChoice) {
  const shown = [part(choice.code), part(choice.name), part(choice.description)].filter(Boolean);
  return shown.join(' · ') || part(choice.label) || choice.value || 'Select';
}

function choiceMatches(choice: SelectChoice, needle: string) {
  if (!needle) return true;
  return [choice.code, choice.name, choice.description, choice.label].some((item) => part(item).toLowerCase().includes(needle));
}

function usePhone() {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 767px)');
    const apply = () => setPhone(query.matches);
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, []);
  return phone;
}

export function Select({ className, children, searchable, choices, onOpen, value, onChange, disabled, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { searchable?: boolean; choices?: SelectChoice[]; onOpen?: () => Promise<void> }) {
  const phone = usePhone();
  const options = useMemo(() => choices || optionList(children), [choices, children]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  if (!phone && !searchable && options.length <= 10 && !onOpen) return <select className={cn(field, className)} value={value} onChange={onChange} disabled={disabled} {...props}>{children}</select>;
  const selected = options.find((option) => option.value === String(value ?? ''));
  const needle = query.trim().toLowerCase();
  const shown = options.filter((option) => choiceMatches(option, needle));
  const detailed = options.some((option) => part(option.code) || part(option.name) || part(option.description));
  const showCode = options.some((option) => part(option.code));
  const showName = options.some((option) => part(option.name));
  const showDescription = options.some((option) => part(option.description));
  const columns = [showCode ? 'minmax(5rem,8rem)' : '', showName ? 'minmax(0,1fr)' : '', showDescription ? 'minmax(0,1.4fr)' : ''].filter(Boolean).join(' ');
  function choose(next: string) {
    onChange?.({ target: { value: next } } as ChangeEvent<HTMLSelectElement>);
    setOpen(false);
  }
  async function openList() {
    setQuery('');
    setLoadError('');
    setOpen(true);
    if (!onOpen) return;
    setLoading(true);
    try {
      await onOpen();
    } catch {
      setLoadError('Could not refresh the list.');
    } finally {
      setLoading(false);
    }
  }
  return (
    <>
      <button type="button" disabled={disabled} className={cn(field, 'h-auto min-h-10 whitespace-normal break-words text-left md:h-10 md:truncate md:whitespace-nowrap', className)} onClick={() => void openList()}>{selected ? choiceLabel(selected) : 'Select'}</button>
      {open ? (
        <Dialog large title={props['aria-label'] ? String(props['aria-label']) : 'Select'} onClose={() => setOpen(false)}>
          <input autoFocus aria-label={detailed ? 'Search code, name, or description' : 'Search options'} className={field} placeholder={detailed ? 'Search code, name, or description' : 'Search'} value={query} onChange={(event) => setQuery(event.target.value)} />
          {loadError ? <p className="text-sm text-[var(--bo-muted)]">{loadError}</p> : null}
          <div className="max-h-[70vh] overflow-y-auto">
            {loading ? <p className="px-3 py-2 text-sm text-[var(--bo-muted)]">Loading</p> : null}
            {!loading && detailed && (showCode || showName || showDescription) ? (
              <div className="grid gap-3 px-3 py-2 text-xs text-[var(--bo-muted)]" style={{ gridTemplateColumns: columns }}>
                {showCode ? <span>Code</span> : null}
                {showName ? <span>Name</span> : null}
                {showDescription ? <span>Description</span> : null}
              </div>
            ) : null}
            {!loading ? shown.map((option, index) => (
              detailed && (part(option.code) || part(option.name) || part(option.description)) ? (
                <button key={`${index}-${option.value}`} type="button" className={`grid w-full gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-[var(--bo-hover)] ${option.value === String(value ?? '') ? 'bg-[var(--bo-hover)]' : ''}`} style={{ gridTemplateColumns: columns }} onClick={() => choose(option.value)}>
                  {showCode ? <span className="truncate">{part(option.code)}</span> : null}
                  {showName ? <span className="truncate">{part(option.name)}</span> : null}
                  {showDescription ? <span className="truncate">{part(option.description)}</span> : null}
                </button>
              ) : (
                <button key={`${index}-${option.value}`} type="button" className={`block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-[var(--bo-hover)] ${option.value === String(value ?? '') ? 'bg-[var(--bo-hover)]' : ''}`} onClick={() => choose(option.value)}>{choiceLabel(option)}</button>
              )
            )) : null}
            {!loading && !shown.length ? <p className="px-3 py-2 text-sm text-[var(--bo-muted)]">No matches.</p> : null}
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="block text-sm"><span className="mb-1.5 block text-[13px] font-medium text-[var(--bo-muted)]">{label}</span>{children}</label>;
}
